/**
 * Client rename (PRJCBUGEN-66396).
 *
 * A custom client name lives in Insight only: it is never pushed to a device, and the name the
 * device reports keeps arriving as it does today. The backend returns one name per client in
 * `clientName`: the custom name when there is one, otherwise the existing name.
 */

export const CLIENT_NAME_MAX_LENGTH = 32;

/** Result codes the rename call can answer with that already exist on the backend. */
export const CLIENT_NAME_RESPONSE_CODES = {
  NO_WRITE_ACCESS: '11075',
  SUPPORT_USER_NO_WRITE_ACCESS: '44028'
};

// C0, DEL and the C1 range U+0080 to U+009F, as the API's Character.isISOControl.
const isControlCharacter = (character) => {
  const code = character.codePointAt(0);
  return code < 32 || (code >= 127 && code <= 159);
};

/**
 * Check a name typed for a client. Length is counted in characters, not UTF-16 units, so a
 * name in Japanese or with an emoji is measured the way the user sees it.
 *
 * @param {string} value - the name as typed
 * @returns {'empty' | 'tooLong' | 'invalidCharacters' | null} why the name is not allowed, or null when it is
 */
export const validateClientName = (value) => {
  const characters = [...String(value ?? '').trim()];
  if (characters.length === 0) return 'empty';
  if (characters.length > CLIENT_NAME_MAX_LENGTH) return 'tooLong';
  if (characters.some((character) => character === '<' || character === '>' || isControlCharacter(character))) {
    return 'invalidCharacters';
  }
  return null;
};

const NON_HEX_CHARACTERS = /[^0-9a-f]/gi;
const RANDOMIZED_SECOND_DIGITS = new Set(['2', '6', 'A', 'E']);

/**
 * A randomized (private) MAC has the locally administered bit set, which shows as 2, 6, A or E
 * in the second hex digit. Phones and laptops use one per network, and may change it, which
 * is why a custom name can stop applying to the device.
 *
 * @param {string} mac - a MAC address in any common format
 * @returns {boolean}
 */
export const isRandomizedMac = (mac) => {
  const hex = String(mac ?? '').replace(NON_HEX_CHARACTERS, '');
  if (hex.length !== 12) return false;
  return RANDOMIZED_SECOND_DIGITS.has(hex[1].toUpperCase());
};

/** Message id for a failed rename, or null to show the backend's own message. */
export const clientRenameErrorMessageId = (responseCode) => {
  const code = String(responseCode ?? '');
  if (code === CLIENT_NAME_RESPONSE_CODES.NO_WRITE_ACCESS || code === CLIENT_NAME_RESPONSE_CODES.SUPPORT_USER_NO_WRITE_ACCESS) {
    return 'clientRenameNoPermission';
  }
  return null;
};
